import React, { useEffect, useState, useCallback } from "react";
import api, { apiErr } from "@/lib/api";
import { useI18n } from "@/i18n";
import { useAuth } from "@/context/AuthContext";
import { useParams, Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { EntityDocuments } from "@/pages/documents/EntityDocuments";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { UserCog, Users, ShieldCheck, IdCard, Clock, FileText, Home, Pencil, Archive } from "lucide-react";
import { toast } from "sonner";
import EntityDialog from "@/pages/people/EntityDialog";

const RIGHTS = ["can_view_grades", "can_view_finance", "can_view_documents", "can_pickup", "is_primary",
  "is_legal_guardian", "is_payer", "can_sign_documents", "can_receive_reminders", "can_submit_documents", "can_receive_urgent_notifications"];

export default function ParentProfile() {
  const { t } = useI18n();
  const { hasRole } = useAuth();
  const { id } = useParams();
  const canManage = hasRole("super_admin", "direction", "administration", "coordinateur");
  const [p, setP] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [editOpen, setEditOpen] = useState(false);
  const saveEdit = async (v) => { try { await api.put(`/people/parents/${id}`, { name: v.name, email: v.email, phone: v.phone, status: v.status }); toast.success(t("save")); load(); } catch (e) { toast.error(apiErr(e.response?.data?.detail)); } };
  const archiveParent = async () => { if (!window.confirm(t("archiveConfirm"))) return; try { await api.patch(`/people/parents/${id}/archive`); toast.success(t("save")); load(); } catch (e) { toast.error(apiErr(e.response?.data?.detail)); } };

  const load = useCallback(() => {
    api.get(`/people/parents/${id}/profile`).then((r) => setP(r.data)).catch((e) => toast.error(apiErr(e.response?.data?.detail)));
    api.get(`/people/parents/${id}/timeline`).then((r) => setTimeline(r.data)).catch(() => {});
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const toggleRight = async (child, key) => {
    const next = { ...child.rights, [key]: !child.rights[key] };
    setP((prev) => ({ ...prev, children: prev.children.map((c) => c.id === child.id ? { ...c, rights: next } : c) }));
    try { await api.put(`/people/parents/${id}/children/${child.id}/rights`, next); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); load(); }
  };

  if (!p) return <div className="text-slate-400">{t("loading")}</div>;
  return (
    <div data-testid="parent-profile">
      <div className="mb-6 flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-3">
          <UserCog className="h-6 w-6 text-primary" />
          <div>
            <h1 className="text-2xl font-display font-bold text-slate-900">{p.name}</h1>
            <p className="text-sm text-slate-500">{p.email || "—"} · {p.phone || "—"} · {p.family?.name || "—"}</p>
          </div>
        </div>
        {canManage && (
          <div className="flex gap-2">
            <Button size="sm" variant="outline" className="gap-1" data-testid="parent-edit-btn" onClick={() => setEditOpen(true)}><Pencil className="h-4 w-4" />{t("editParent")}</Button>
            <Button size="sm" variant="outline" className="gap-1" data-testid="parent-archive-btn" onClick={archiveParent}><Archive className="h-4 w-4" />{t("archive")}</Button>
          </div>
        )}
      </div>
      <EntityDialog open={editOpen} onOpenChange={setEditOpen} title={t("editParent")} onSubmit={saveEdit}
        initial={{ name: p.name, email: p.email, phone: p.phone, status: p.status }}
        fields={[{ name: "name", label: t("name") }, { name: "email", label: t("email") }, { name: "phone", label: t("phone") }, { name: "status", label: t("status") }]} />
      <Tabs defaultValue="overview">
        <TabsList className="flex-wrap h-auto">
          <TabsTrigger value="overview" data-testid="tab-poverview"><UserCog className="h-4 w-4 mr-2" />{t("overview")}</TabsTrigger>
          <TabsTrigger value="children" data-testid="tab-pchildren"><Users className="h-4 w-4 mr-2" />{t("children")}</TabsTrigger>
          <TabsTrigger value="rights" data-testid="tab-prights"><ShieldCheck className="h-4 w-4 mr-2" />{t("parentsRightsFull")}</TabsTrigger>
          <TabsTrigger value="account" data-testid="tab-paccount"><IdCard className="h-4 w-4 mr-2" />{t("account")}</TabsTrigger>
          <TabsTrigger value="documents" data-testid="tab-pdocuments"><FileText className="h-4 w-4 mr-2" />{t("documentsTab")}</TabsTrigger>
          <TabsTrigger value="timeline" data-testid="tab-ptimeline"><Clock className="h-4 w-4 mr-2" />{t("timeline")}</TabsTrigger>
        </TabsList>

        <TabsContent value="overview">
          <Card className="border-slate-200 p-5 max-w-lg space-y-2 text-sm" data-testid="parent-overview">
            <div className="flex justify-between"><span className="text-slate-500">{t("email")}</span><span className="text-slate-800">{p.email || "—"}</span></div>
            <div className="flex justify-between"><span className="text-slate-500">{t("phone")}</span><span className="text-slate-800">{p.phone || "—"}</span></div>
            <div className="flex justify-between"><span className="text-slate-500">{t("status")}</span><span className="text-slate-800">{p.status || "active"}</span></div>
            <div className="flex justify-between items-center"><span className="text-slate-500">{t("family")}</span>
              {p.family ? <Link to={`/people/families/${p.family.id}`} data-testid="parent-family-link"><Button size="sm" variant="outline" className="gap-1"><Home className="h-3.5 w-3.5" />{p.family.name}</Button></Link> : <span>—</span>}
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="children">
          <div className="grid sm:grid-cols-2 gap-3" data-testid="parent-children">
            {p.children.length === 0 ? <p className="text-sm text-slate-400">{t("noData")}</p> :
              p.children.map((c) => (
                <Card key={c.id} className="border-slate-200 p-4 flex items-center justify-between" data-testid="parent-child-card">
                  <div>
                    <div className="font-semibold text-slate-800">{c.name}</div>
                    <div className="text-xs text-slate-500">{c.level_name || "—"} · {c.current_group?.group_name || "—"}</div>
                    <Badge className={`mt-1 border-0 ${c.rights.linked ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>{c.rights.linked ? t("linked") : t("notLinked")}</Badge>
                  </div>
                  <Link to={`/people/students/${c.id}`}><Button size="sm" variant="outline" className="gap-1"><IdCard className="h-3.5 w-3.5" />{t("viewFile")}</Button></Link>
                </Card>))}
          </div>
        </TabsContent>

        <TabsContent value="rights">
          <div className="space-y-3" data-testid="parent-rights">
            {p.children.length === 0 ? <p className="text-sm text-slate-400">{t("noData")}</p> :
              p.children.map((c) => (
                <Card key={c.id} className="border-slate-200 p-4" data-testid="parent-rights-card">
                  <div className="font-semibold text-slate-800 mb-3">{c.name}</div>
                  <div className="grid sm:grid-cols-2 gap-2">
                    {RIGHTS.map((k) => (
                      <label key={k} className="flex items-center justify-between text-sm text-slate-600 rounded-lg border border-slate-100 px-3 py-2">
                        {t(k)}
                        <Switch data-testid={`pright-${c.id}-${k}`} checked={!!c.rights[k]} disabled={!canManage} onCheckedChange={() => toggleRight(c, k)} />
                      </label>
                    ))}
                  </div>
                </Card>))}
          </div>
        </TabsContent>

        <TabsContent value="account">
          <Card className="border-slate-200 p-5 max-w-lg space-y-2 text-sm" data-testid="parent-account">
            {p.user_account ? (<>
              <div className="flex justify-between"><span className="text-slate-500">{t("email")}</span><span className="text-slate-800">{p.user_account.email}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">{t("status")}</span><Badge className="border-0 bg-slate-100">{p.user_account.status || "—"}</Badge></div>
              <div className="flex justify-between"><span className="text-slate-500">Roles</span><span className="text-slate-800">{(p.user_account.roles || []).join(", ")}</span></div>
            </>) : <p className="text-slate-400">{t("noData")}</p>}
          </Card>
        </TabsContent>

        <TabsContent value="documents">
          <EntityDocuments filterKey="parent_id" id={id} testid="parent-documents" />
        </TabsContent>

        <TabsContent value="timeline">
          <div className="space-y-2" data-testid="parent-timeline">
            {timeline.length === 0 ? <p className="text-sm text-slate-400">{t("noData")}</p> :
              timeline.map((it, i) => (
                <div key={i} className="flex gap-3 items-start rounded-lg border border-slate-100 px-3 py-2">
                  <div className="h-2 w-2 rounded-full bg-primary mt-2" />
                  <div><div className="text-sm font-medium text-slate-800">{it.title}</div>
                    <div className="text-xs text-slate-500">{(it.date || "").slice(0, 16).replace("T", " ")}{it.detail ? ` · ${it.detail}` : ""}</div></div>
                </div>))}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
